import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import type { CorrectionFormData } from './training';
import { UserMessage, ForemanMessage, SystemMessage } from './messages';
import styles from './ChatArea.module.css';

interface ChatAreaProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  onCorrect?: (messageId: string, data: CorrectionFormData) => void;
  onAction?: (action: string) => void;
}

export function ChatArea({ messages, isStreaming, onCorrect, onAction }: ChatAreaProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  return (
    <div className={styles.chatArea}>
      <div className={styles.messages}>
        {messages.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>&#x26CF;</div>
            <div className={styles.emptyTitle}>Holler at Foreman</div>
            <div className={styles.emptyText}>
              Ask a question, submit a scope for pricing, or use the quick actions below.
            </div>
          </div>
        )}
        {messages.map((msg) => {
          if (msg.type === 'user') {
            return <UserMessage key={msg.id} message={msg} />;
          }
          if (msg.type === 'foreman') {
            return (
              <ForemanMessage
                key={msg.id}
                message={msg}
                onCorrect={onCorrect}
                onAction={onAction}
              />
            );
          }
          if (msg.type === 'system') {
            return <SystemMessage key={msg.id} message={msg} />;
          }
          return null;
        })}
        {isStreaming && (
          <div className={styles.typing}>
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
